import {
  useCallback,
  useEffect,
  useMemo,
  useRef,
  useState,
} from "react";

import {
  FaWhatsapp,
  FaEnvelope,
  FaSms,
  FaPhone,
} from "react-icons/fa";

import useAuth from "../../../hooks/useAuth";
import { actsAsAgencyAdmin } from "../../../utils/permissions";
import { formatVehicleNumberDisplay } from "../../../utils/vehicleNumberUtils";

import {
  filterFieldsByRole,
  shouldShowAdminOnlyCaseFields,
} from "../../../utils/caseFieldVisibility";

import { useEnrichedAdminCase } from "../../../hooks/useEnrichedAdminCase";
import AdminExcelFieldGrid from "../../../components/repo/AdminExcelFieldGrid";
import { repoCaseService } from "../../../services/repoCase.service";
import confirmationService from "../../../services/confirmation.service";
import { isMongoCaseId } from "./findVehiclesHelpers";
import { getBankerFieldsFromCase } from "../../../utils/bankerExcelFields";
import { openPhoneCall } from "../../../utils/bankNotifyShare";
import { resolveVehicleDisplayStatus } from "../../../utils/vehicleDisplayStatus";
import { isOfflineWorkMode } from "../../../constants/workMode";
import { resolveRepoIdentity } from "../../../offline/offlineIdentity";
import {
  loadWhatsAppRecipients,
  saveWhatsAppRecipients,
} from "../../../offline/whatsappRecipientsCache";

const MAX_WHATSAPP_RECIPIENTS = 4;

const BANKER_DETAIL_FIELDS = [
  { key: "banker1Name", label: "1st bankar Name" },
  { key: "banker1Phone", label: "mobile no 1" },
  { key: "banker2Name", label: "2nd bankar Name" },
  { key: "banker2Phone", label: "mobile no 2" },
  { key: "banker3Name", label: "3rd bankar Name" },
  { key: "banker3Phone", label: "mobile no 3" },
];

const ADMIN_GRID_OMIT_LABELS = new Set(
  BANKER_DETAIL_FIELDS.map((field) => field.label)
);

const safeValue = (value) => {
  if (
    value === null ||
    value === undefined ||
    value === ""
  ) {
    return "—";
  }

  return value;
};

const formatMoney = (value) => {
  if (
    value === null ||
    value === undefined ||
    value === ""
  ) {
    return "—";
  }

  const num = Number(value);

  if (Number.isNaN(num)) {
    return safeValue(value);
  }

  return num.toLocaleString(undefined, {
    maximumFractionDigits: 2,
  });
};

const DETAIL_FIELDS = [
  {
    key: "vehicleNumber",
    label: "Vehicle Number",
  },
  {
    key: "customerName",
    label: "Customer Name",
  },
  {
    key: "mobileNumber",
    label: "Mobile Number",
  },
  {
    key: "alternateMobileNumber",
    label: "Alternate Mobile",
  },
  {
    key: "loanAccountNumber",
    label: "Loan Account",
  },
  {
    key: "referenceNumber",
    label: "Reference Number",
  },
  {
    key: "engineNumber",
    label: "Engine Number",
  },
  {
    key: "chassisNumber",
    label: "Chassis Number",
  },
  {
    key: "vehicleBrand",
    label: "Brand",
  },
  {
    key: "vehicleModel",
    label: "Model",
  },
  {
    key: "vehicleType",
    label: "Vehicle Type",
  },
  {
    key: "bankName",
    label: "Bank Name",
  },
  {
    key: "branchName",
    label: "Branch Name",
  },
  {
    key: "city",
    label: "City",
  },
  {
    key: "state",
    label: "State",
  },
  {
    key: "pincode",
    label: "Pincode",
  },
  {
    key: "emiAmount",
    label: "EMI Amount",
    format: formatMoney,
  },
  {
    key: "dueAmount",
    label: "Due Amount",
    format: formatMoney,
  },
  {
    key: "totalOutstandingAmount",
    label: "Outstanding",
    format: formatMoney,
  },
  {
    key: "bucket",
    label: "Bucket",
  },
  {
    key: "displayStatus",
    label: "Status",
  },
];

export default function VehicleDetailModal({
  item,
  onClose,
  onShare,
  shareBusy = false,
  onLoadedSaved,
  className = "",
  overlayClassName = "",
  traceReport = null,
}) {
  const { auth } = useAuth();

  const isRepoAdmin =
    actsAsAgencyAdmin(auth) ||
    shouldShowAdminOnlyCaseFields(
      auth?.user?.role
    );

  /*
   * --------------------------------------------------
   * CASE ENRICHMENT
   * --------------------------------------------------
   */

  const caseIdForEnrich =
    isMongoCaseId(
      item?._id || item?.id
    )
      ? item?._id || item?.id
      : null;

  const {
    enrichedCase,
    enriching,
  } = useEnrichedAdminCase(item, {
    token: auth?.token,
    caseId: caseIdForEnrich,
    enabled:
      isRepoAdmin &&
      Boolean(item) &&
      Boolean(auth?.token) &&
      !isOfflineWorkMode() &&
      navigator.onLine !== false,
  });

  /*
   * Admin:
   * Use enriched case when available.
   *
   * Agent/User:
   * Use original item.
   */

  const [liveStatus, setLiveStatus] = useState(null);

  useEffect(() => {
    if (
      !auth?.token ||
      !item?.vehicleNumber ||
      isOfflineWorkMode() ||
      navigator.onLine === false
    ) {
      setLiveStatus(null);
      return undefined;
    }

    let cancelled = false;
    repoCaseService
      .searchTracesByVehicleNumber(item.vehicleNumber, auth.token)
      .then((res) => {
        if (cancelled) return;
        const rows = Array.isArray(res?.items)
          ? res.items
          : Array.isArray(res?.data?.items)
            ? res.data.items
            : [];
        if (!rows.length) return;
        const ranked = [...rows].sort((a, b) => {
          const score = (row) => {
            if (String(row.repoStatus || "").toUpperCase() === "SEIZED") return 4;
            const status = String(
              row.latestTrace?.status || row.confirmationStatus || ""
            ).toUpperCase();
            if (status === "CONFIRMED") return 3;
            if (status === "PENDING") return 2;
            if (status === "REJECTED") return 1;
            return 0;
          };
          return score(b) - score(a);
        });
        const best = ranked[0];
        setLiveStatus({
          confirmationStatus:
            best.latestTrace?.status || best.confirmationStatus || "",
          repoStatus: best.repoStatus || "",
          inventoryConfirmed: Boolean(best.inventoryConfirmed),
        });
      })
      .catch(() => {
        if (!cancelled) setLiveStatus(null);
      });

    return () => {
      cancelled = true;
    };
  }, [auth?.token, item?.vehicleNumber]);

  const displayItem = {
    ...(isRepoAdmin ? enrichedCase || item : item),
    ...(liveStatus || {}),
  };

  /*
   * --------------------------------------------------
   * BANKER FIELDS
   * --------------------------------------------------
   *
   * Important:
   * Banker fields are taken from displayItem.
   *
   * For Admin:
   * These are used as WhatsApp recipients.
   *
   * For Agent/User:
   * These are only displayed in case details.
   */

  const bankerFields = useMemo(
    () =>
      getBankerFieldsFromCase(
        displayItem || {}
      ),
    [displayItem]
  );

  /*
   * --------------------------------------------------
   * LOADED DETAILS STATE
   * --------------------------------------------------
   */

  const [
    loadedDetail,
    setLoadedDetail,
  ] = useState(
    () =>
      item?.loadedDetail ||
      item?.loadedShort ||
      ""
  );

  const [
    loadedLoading,
    setLoadedLoading,
  ] = useState(false);

  const [
    loadedSaving,
    setLoadedSaving,
  ] = useState(false);

  const [
    loadedError,
    setLoadedError,
  ] = useState("");

  const [
    loadedSavedMsg,
    setLoadedSavedMsg,
  ] = useState("");

  const [
    loadedDirty,
    setLoadedDirty,
  ] = useState(false);

  /*
   * --------------------------------------------------
   * WHATSAPP MENU STATE
   * --------------------------------------------------
   */

  const [
    shareMenu,
    setShareMenu,
  ] = useState(null);

  const whatsappMenuRef =
    useRef(null);

  const smsMenuRef =
    useRef(null);

  const callMenuRef =
    useRef(null);

  /*
   * --------------------------------------------------
   * WHATSAPP MULTI-SELECT STATE
   * --------------------------------------------------
   */

  const [
    selectedWhatsappRecipients,
    setSelectedWhatsappRecipients,
  ] = useState([]);

  /*
   * --------------------------------------------------
   * WHATSAPP SENDING STATE
   * --------------------------------------------------
   */

  const [
    whatsappSending,
    setWhatsappSending,
  ] = useState(false);

  /*
   * --------------------------------------------------
   * WHATSAPP RECIPIENTS STATE
   * --------------------------------------------------
   *
   * Agent/User panel:
   * API returns:
   *
   * Primary Admin
   * +
   * Active Agents
   *
   * Admin panel:
   * We DO NOT use this API.
   *
   * Admin uses current case banker numbers.
   */

  const [
    whatsappRecipients,
    setWhatsappRecipients,
  ] = useState([]);

  const [
    whatsappRecipientsLoading,
    setWhatsappRecipientsLoading,
  ] = useState(false);

  /*
   * --------------------------------------------------
   * LOAD WHATSAPP RECIPIENTS
   * --------------------------------------------------
   *
   * Only Agent/User panel loads the API.
   *
   * Admin panel uses banker fields from case.
   */

  useEffect(() => {
    const { userId, companyId } = resolveRepoIdentity(auth?.user);
    const cached = loadWhatsAppRecipients({ userId, companyId });

    if (!auth?.token) {
      setWhatsappRecipients(cached);
      setWhatsappRecipientsLoading(false);
      return undefined;
    }

    /*
     * Admin does not need the generic
     * Admin + Agent recipient API.
     */

    if (isRepoAdmin) {
      setWhatsappRecipients([]);
      setWhatsappRecipientsLoading(false);
      return undefined;
    }

    if (cached.length) {
      setWhatsappRecipients(cached);
    }

    if (isOfflineWorkMode() || navigator.onLine === false) {
      setWhatsappRecipientsLoading(false);
      return undefined;
    }

    let cancelled = false;

    const fetchWhatsAppRecipients =
      async () => {
        setWhatsappRecipientsLoading(
          !cached.length
        );

        try {
          const res =
            await confirmationService.getWhatsAppRecipients(
              auth.token
            );

          const recipients =
            Array.isArray(
              res?.data?.adminRecipients
            )
              ? res.data.adminRecipients
              : Array.isArray(
                    res?.adminRecipients
                  )
                ? res.adminRecipients
                : [];

          if (!cancelled) {
            setWhatsappRecipients(
              recipients
            );
            saveWhatsAppRecipients({
              userId,
              companyId,
              recipients,
            });
          }
        } catch (err) {
          console.error(
            "[VehicleDetailModal] Failed to load WhatsApp recipients:",
            err
          );

          if (!cancelled && !cached.length) {
            setWhatsappRecipients([]);
          }
        } finally {
          if (!cancelled) {
            setWhatsappRecipientsLoading(
              false
            );
          }
        }
      };

    fetchWhatsAppRecipients();

    return () => {
      cancelled = true;
    };
  }, [
    auth?.token,
    auth?.user?.id,
    auth?.user?._id,
    auth?.user?.companyId,
    isRepoAdmin,
  ]);

  /*
   * --------------------------------------------------
   * TRACE REPORT
   * --------------------------------------------------
   *
   * Used as fallback for Agent/User recipient list.
   */

  const effectiveTraceReport = useMemo(() => {
    const directTraceReport =
      traceReport &&
      typeof traceReport === "object"
        ? traceReport
        : null;

    /*
     * Case 1:
     * Direct traceReport.
     */

    if (
      Array.isArray(
        directTraceReport?.adminRecipients
      )
    ) {
      return directTraceReport;
    }

    /*
     * Case 2:
     * traceReport.data.traceReport
     */

    if (
      directTraceReport?.data?.traceReport &&
      typeof directTraceReport.data
        .traceReport === "object"
    ) {
      return directTraceReport.data
        .traceReport;
    }

    /*
     * Case 3:
     * item.traceReport
     */

    if (
      item?.traceReport &&
      typeof item.traceReport === "object"
    ) {
      return item.traceReport;
    }

    /*
     * Case 4:
     * item.data.traceReport
     */

    if (
      item?.data?.traceReport &&
      typeof item.data.traceReport ===
        "object"
    ) {
      return item.data.traceReport;
    }

    return null;
  }, [
    traceReport,
    item,
  ]);

  /*
   * --------------------------------------------------
   * WHATSAPP RECIPIENT OPTIONS
   * --------------------------------------------------
   *
   * ADMIN:
   *
   * Only banker numbers from current case.
   *
   * 1st Banker
   * 2nd Banker
   * 3rd Banker
   *
   * AGENT / USER:
   *
   * Existing API behavior remains:
   *
   * Primary Admin
   * +
   * Agents / Users
   */

  const whatsappOptions = useMemo(() => {
    /*
     * ==================================================
     * ADMIN PANEL
     * ==================================================
     */

    if (isRepoAdmin) {
      const bankerRecipients = [
        {
          id: "banker-1",
          name:
            bankerFields?.banker1Name ||
            "1st Banker",
          phone:
            bankerFields?.banker1Phone ||
            "",
          role: "BANKER",
          roleLabel: "Banker",
          recipientType: "BANKER",
          isPrimaryAdmin: false,
        },

        {
          id: "banker-2",
          name:
            bankerFields?.banker2Name ||
            "2nd Banker",
          phone:
            bankerFields?.banker2Phone ||
            "",
          role: "BANKER",
          roleLabel: "Banker",
          recipientType: "BANKER",
          isPrimaryAdmin: false,
        },

        {
          id: "banker-3",
          name:
            bankerFields?.banker3Name ||
            "3rd Banker",
          phone:
            bankerFields?.banker3Phone ||
            "",
          role: "BANKER",
          roleLabel: "Banker",
          recipientType: "BANKER",
          isPrimaryAdmin: false,
        },
      ];

      const seen = new Set();
      const options = [];

      for (
        let index = 0;
        index <
        bankerRecipients.length;
        index += 1
      ) {
        const recipient =
          bankerRecipients[index];

        if (!recipient) {
          continue;
        }

        const rawPhone = String(
          recipient.phone || ""
        ).trim();

        const digitsOnly =
          rawPhone.replace(/\D/g, "");

        /*
         * Ignore banker if phone number
         * does not exist.
         */

        if (
          digitsOnly.length < 10
        ) {
          continue;
        }

        /*
         * Duplicate detection.
         *
         * Example:
         * 9654008500
         * +91 9654008500
         *
         * should be treated as same number.
         */

        const phoneKey =
          digitsOnly.slice(-10);

        if (seen.has(phoneKey)) {
          continue;
        }

        seen.add(phoneKey);

        /*
         * Convert Indian number to WhatsApp
         * international format.
         */

        let whatsappPhone =
          digitsOnly;

        if (
          digitsOnly.length === 10
        ) {
          whatsappPhone =
            `91${digitsOnly}`;
        } else if (
          digitsOnly.startsWith("91") &&
          digitsOnly.length === 12
        ) {
          whatsappPhone =
            digitsOnly;
        }

        const name =
          String(
            recipient.name || ""
          ).trim();

        const displayName =
          name &&
          name !== "—"
            ? name
            : recipient.id ===
                "banker-1"
              ? "1st Banker"
              : recipient.id ===
                  "banker-2"
                ? "2nd Banker"
                : "3rd Banker";

        options.push({
          id: recipient.id,

          label:
            `Banker - ${displayName}`,

          phone: rawPhone,

          whatsappPhone,

          role: "BANKER",

          roleLabel: "Banker",

          recipientType: "BANKER",

          email: "",

          isPrimaryAdmin: false,
        });
      }

      return options;
    }

    /*
     * ==================================================
     * AGENT / USER PANEL
     * ==================================================
     *
     * Existing behavior.
     */

    const apiRecipients =
      Array.isArray(
        whatsappRecipients
      )
        ? whatsappRecipients
        : [];

    const fallbackRecipients =
      Array.isArray(
        effectiveTraceReport?.adminRecipients
      )
        ? effectiveTraceReport.adminRecipients
        : [];

    /*
     * Prefer dedicated API response.
     *
     * Fallback to traceReport only when
     * API has no recipients.
     */

    const recipients =
      apiRecipients.length > 0
        ? apiRecipients
        : fallbackRecipients;

    const seen = new Set();
    const options = [];

    for (
      let index = 0;
      index < recipients.length;
      index += 1
    ) {
      const recipient =
        recipients[index];

      if (!recipient) {
        continue;
      }

      const rawPhone = String(
        recipient.phone || ""
      ).trim();

      const digitsOnly =
        rawPhone.replace(/\D/g, "");

      if (
        digitsOnly.length < 10
      ) {
        continue;
      }

      /*
       * Last 10 digits for duplicate
       * detection.
       */

      const phoneKey =
        digitsOnly.slice(-10);

      if (seen.has(phoneKey)) {
        continue;
      }

      seen.add(phoneKey);

      /*
       * WhatsApp country code.
       */

      let whatsappPhone =
        digitsOnly;

      if (
        digitsOnly.length === 10
      ) {
        whatsappPhone =
          `91${digitsOnly}`;
      } else if (
        digitsOnly.startsWith("91") &&
        digitsOnly.length === 12
      ) {
        whatsappPhone =
          digitsOnly;
      }

      const isPrimaryAdmin =
        Boolean(
          recipient.isPrimaryAdmin
        ) ||
        String(
          recipient.role || ""
        ).trim() === "REPO_ADMIN";

      const name =
        String(
          recipient.name || ""
        ).trim();

      const role =
        String(
          recipient.role || ""
        ).trim();

      const roleLabel =
        String(
          recipient.roleLabel ||
            role ||
            "User"
        ).trim();

      options.push({
        id:
          recipient.id ||
          recipient._id ||
          `recipient-${index}`,

        label:
          isPrimaryAdmin
            ? name
              ? `Admin - ${name}`
              : "Admin"
            : name
              ? `Agent - ${name}`
              : "Agent",

        phone: rawPhone,

        whatsappPhone,

        role,

        roleLabel,

        recipientType:
          recipient.recipientType ||
          "USER",

        email:
          String(
            recipient.email || ""
          ).trim(),

        isPrimaryAdmin,
      });
    }

    options.sort((a, b) => {
      if (a.isPrimaryAdmin === b.isPrimaryAdmin) return 0;
      return a.isPrimaryAdmin ? -1 : 1;
    });

    return options;
  }, [
    isRepoAdmin,
    bankerFields,
    whatsappRecipients,
    effectiveTraceReport,
  ]);

  /*
   * --------------------------------------------------
   * SELECTED WHATSAPP OPTIONS
   * --------------------------------------------------
   */

  const selectedWhatsappOptions =
    useMemo(() => {
      const selectedIds =
        new Set(
          selectedWhatsappRecipients.map(
            String
          )
        );

      return whatsappOptions.filter(
        (option) =>
          selectedIds.has(
            String(option.id)
          )
      );
    }, [
      selectedWhatsappRecipients,
      whatsappOptions,
    ]);

  /*
   * --------------------------------------------------
   * DETAIL FIELDS
   * --------------------------------------------------
   */

  const detailFields =
    filterFieldsByRole(
      DETAIL_FIELDS,
      auth?.user?.role
    ).filter(
      (field) =>
        !isRepoAdmin ||
        ![
          "mobileNumber",
          "alternateMobileNumber",
          "loanAccountNumber",
        ].includes(field.key)
    );

  /*
   * --------------------------------------------------
   * CLOSE MENU / RESET SELECTION
   * WHEN VEHICLE CHANGES
   * --------------------------------------------------
   */

  useEffect(() => {
    setShareMenu(null);

    setSelectedWhatsappRecipients(
      []
    );
  }, [
    item?._id,
    item?.id,
    item?.vehicleNumber,
  ]);

  /*
   * --------------------------------------------------
   * CLOSE DROPDOWN OUTSIDE / ESC
   * --------------------------------------------------
   */

  useEffect(() => {
    if (!shareMenu) {
      return undefined;
    }

    const onPointerDown = (
      event
    ) => {
      const openMenuNode =
        shareMenu === "sms"
          ? smsMenuRef.current
          : shareMenu === "call"
            ? callMenuRef.current
            : whatsappMenuRef.current;
      if (
        !openMenuNode?.contains(
          event.target
        )
      ) {
        setShareMenu(null);
      }
    };

    const onKeyDown = (event) => {
      if (
        event.key === "Escape"
      ) {
        setShareMenu(null);
      }
    };

    document.addEventListener(
      "mousedown",
      onPointerDown
    );

    document.addEventListener(
      "touchstart",
      onPointerDown
    );

    document.addEventListener(
      "keydown",
      onKeyDown
    );

    return () => {
      document.removeEventListener(
        "mousedown",
        onPointerDown
      );

      document.removeEventListener(
        "touchstart",
        onPointerDown
      );

      document.removeEventListener(
        "keydown",
        onKeyDown
      );
    };
  }, [
    shareMenu,
  ]);

  /*
   * --------------------------------------------------
   * LOAD VEHICLE LOADED DETAILS
   * --------------------------------------------------
   */

  const fetchLoaded =
    useCallback(async () => {
      if (
        !auth?.token ||
        !item ||
        isOfflineWorkMode() ||
        navigator.onLine === false
      ) {
        return;
      }

      setLoadedLoading(true);
      setLoadedError("");

      try {
        const res =
          await repoCaseService.getVehicleLoaded(
            auth.token,
            {
              vehicleNumber:
                item.vehicleNumber ||
                "",

              chassisNumber:
                item.chassisNumber ||
                "",

              caseId:
                isMongoCaseId(
                  item._id ||
                    item.id
                )
                  ? item._id ||
                    item.id
                  : undefined,
            }
          );

        const data =
          res?.data || res;

        setLoadedDetail(
          data?.loadedDetail ||
            data?.loadedShort ||
            ""
        );

        setLoadedDirty(false);
      } catch (err) {
        console.error(
          "[VehicleDetailModal] fetchLoaded error:",
          err
        );

        setLoadedError(
          err?.response?.data
            ?.message ||
            "Could not load vehicle loaded details."
        );
      } finally {
        setLoadedLoading(false);
      }
    }, [
      auth?.token,
      item,
    ]);

  /*
   * --------------------------------------------------
   * RESET / LOAD DETAILS
   * --------------------------------------------------
   */

  useEffect(() => {
    if (!item) {
      return;
    }

    setLoadedDetail(
      item.loadedDetail ||
        item.loadedShort ||
        ""
    );

    setLoadedSavedMsg("");
    setLoadedDirty(false);

    fetchLoaded();
  }, [
    item,
    fetchLoaded,
  ]);

  /*
   * --------------------------------------------------
   * SAVE LOADED DETAILS
   * --------------------------------------------------
   */

  const handleSaveLoaded =
    async () => {
      if (
        !auth?.token ||
        !item ||
        (!loadedDirty &&
          !loadedSaving)
      ) {
        return;
      }

      setLoadedSaving(true);
      setLoadedError("");
      setLoadedSavedMsg("");

      try {
        const res =
          await repoCaseService.saveVehicleLoaded(
            auth.token,
            {
              vehicleNumber:
                item.vehicleNumber ||
                "",

              chassisNumber:
                item.chassisNumber ||
                "",

              caseId:
                isMongoCaseId(
                  item._id ||
                    item.id
                )
                  ? item._id ||
                    item.id
                  : undefined,

              loadedShort: "",
              loadedDetail,
            }
          );

        const data =
          res?.data || res;

        const patch = {
          loadedShort: "",
          loadedDetail:
            data?.loadedDetail ??
            loadedDetail,
        };

        onLoadedSaved?.(
          item,
          patch
        );

        setLoadedDirty(false);

        setLoadedSavedMsg(
          "Saved"
        );

        setTimeout(() => {
          setLoadedSavedMsg("");
        }, 1500);
      } catch (err) {
        console.error(
          "[VehicleDetailModal] saveLoaded error:",
          err
        );

        setLoadedError(
          err?.response?.data
            ?.message ||
            "Could not save loaded details."
        );
      } finally {
        setLoadedSaving(false);
      }
    };

  /*
   * --------------------------------------------------
   * DETAIL ROWS
   * --------------------------------------------------
   */

  const sortedDetailRows =
    useMemo(() => {
      if (!displayItem) {
        return [];
      }

      if (isRepoAdmin) {
        return [];
      }

      return detailFields.map(
        ({
          key,
          label,
          format,
        }) => ({
          key,
          label,
          value:
            key === "displayStatus"
              ? resolveVehicleDisplayStatus(displayItem)
              : key ===
            "vehicleNumber"
              ? formatVehicleNumberDisplay(
                  displayItem[key]
                )
              : format
                ? format(
                    displayItem[key]
                  )
                : safeValue(
                    displayItem[key]
                  ),
        })
      );
    }, [
      displayItem,
      detailFields,
      isRepoAdmin,
    ]);

  if (
    !item ||
    !displayItem
  ) {
    return null;
  }

  /*
   * --------------------------------------------------
   * ITEM WITH LOADED DETAILS
   * --------------------------------------------------
   */

  const itemWithLoaded = {
    ...displayItem,
    loadedShort: "",
    loadedDetail,
  };

  /*
   * --------------------------------------------------
   * TOGGLE WHATSAPP RECIPIENT
   * --------------------------------------------------
   */

  const toggleWhatsappRecipient = (
    recipient
  ) => {
    if (
      whatsappSending ||
      shareBusy
    ) {
      return;
    }

    const recipientId =
      String(recipient.id);

    setSelectedWhatsappRecipients(
      (current) => {
        const exists =
          current.includes(
            recipientId
          );

        /*
         * Unselect.
         */

        if (exists) {
          return current.filter(
            (id) =>
              id !== recipientId
          );
        }

        const limit =
          shareMenu === "call"
            ? 1
            : MAX_WHATSAPP_RECIPIENTS;

        if (current.length >= limit) {
          return current;
        }

        return [
          ...current,
          recipientId,
        ];
      }
    );
  };

  /*
   * --------------------------------------------------
   * WHATSAPP SHARE - SINGLE
   * --------------------------------------------------
   */

  const shareWhatsApp = (
    recipient = null,
    channel = "whatsapp"
  ) => {
    setShareMenu(null);

    const phone =
      typeof recipient === "string"
        ? recipient
        : recipient?.phone || "";

    const whatsappPhone =
      typeof recipient === "object"
        ? recipient?.whatsappPhone ||
          phone
        : phone;

    onShare?.(
      channel,
      itemWithLoaded,
      phone
        ? {
            notifyPhone: phone,
            whatsappPhone,
          }
        : {}
    );
  };

  /*
   * --------------------------------------------------
   * WHATSAPP SHARE - MULTIPLE
   * --------------------------------------------------
   *
   * Works for:
   *
   * Admin:
   * Banker 1 + Banker 2 + Banker 3
   *
   * Agent/User:
   * Admin + Users/Agents
   */

  const handleSendToSelectedWhatsapp =
    async () => {
      if (
        whatsappSending ||
        shareBusy ||
        selectedWhatsappOptions.length === 0
      ) {
        return;
      }

      setWhatsappSending(true);
      const channel = shareMenu || "whatsapp";
      setShareMenu(null);

      try {
        if (channel === "call") {
          const recipient = selectedWhatsappOptions.find(
            (row) => row?.phone
          );
          if (!recipient?.phone) {
            window.alert("Select one number to call.");
            return;
          }
          openPhoneCall(recipient.phone);
          return;
        }

        if (isRepoAdmin) {
          const bankers = selectedWhatsappOptions.filter(
            (recipient) => recipient.phone
          );
          const first = bankers[0];
          if (first) {
            await onShare?.(
              channel,
              itemWithLoaded,
              {
                notifyPhone: first.phone,
                whatsappPhone:
                  first.whatsappPhone || first.phone,
                bankerRecipients: bankers,
              }
            );
          }
        } else {
          for (const recipient of selectedWhatsappOptions) {
            const phone = recipient.phone || "";
            const whatsappPhone =
              recipient.whatsappPhone || phone;

            if (!phone) {
              continue;
            }

            try {
              await onShare?.(
                channel,
                itemWithLoaded,
                {
                  notifyPhone: phone,
                  whatsappPhone,
                }
              );
            } catch (err) {
              console.error(
                "[VehicleDetailModal] Share failed for recipient:",
                recipient,
                err
              );
            }
          }
        }
      } finally {
        setWhatsappSending(false);

        setSelectedWhatsappRecipients(
          []
        );
      }
    };

  /*
   * --------------------------------------------------
   * WHATSAPP BUTTON
   * --------------------------------------------------
   */

  const handleWhatsAppButtonClick =
    () => {
      if (
        shareBusy ||
        whatsappSending ||
        whatsappRecipientsLoading
      ) {
        return;
      }

      /*
       * No recipient:
       *
       * Preserve existing fallback.
       */

      if (
        whatsappOptions.length === 0
      ) {
        shareWhatsApp(null, "whatsapp");
        return;
      }

      setShareMenu((open) =>
        open === "whatsapp" ? null : "whatsapp"
      );
    };

  const handleSmsButtonClick =
    () => {
      if (
        shareBusy ||
        whatsappSending ||
        whatsappRecipientsLoading
      ) {
        return;
      }

      if (
        whatsappOptions.length === 0
      ) {
        shareWhatsApp(null, "sms");
        return;
      }

      setShareMenu((open) =>
        open === "sms" ? null : "sms"
      );
    };

  const handleCallButtonClick =
    () => {
      if (
        shareBusy ||
        whatsappSending ||
        whatsappRecipientsLoading
      ) {
        return;
      }

      if (whatsappOptions.length === 0) {
        window.alert(
          "No number is saved locally to call."
        );
        return;
      }

      setSelectedWhatsappRecipients((current) =>
        current.slice(0, 1)
      );
      setShareMenu((open) =>
        open === "call" ? null : "call"
      );
    };

  const renderPhoneShareMenu = (
    channel,
    menuRef,
    BtnIcon,
    btnClass,
    label
  ) => (
          <div
            className={`fv-whatsapp-menu${
              channel === "call" || channel === "sms"
                ? " fv-whatsapp-menu--end"
                : ""
            }`}
            ref={menuRef}
          >
            <button
              type="button"
              className={`fv-btn ${btnClass}`}
              disabled={
                shareBusy ||
                whatsappSending ||
                whatsappRecipientsLoading
              }
              aria-haspopup={
                whatsappOptions.length >
                0
                  ? "menu"
                  : undefined
              }
              aria-expanded={
                whatsappOptions.length >
                0
                  ? shareMenu === channel
                  : undefined
              }
              onClick={
                channel === "sms"
                  ? handleSmsButtonClick
                  : channel === "call"
                    ? handleCallButtonClick
                    : handleWhatsAppButtonClick
              }
            >
              <BtnIcon />

              <span>
                {shareBusy ||
                whatsappSending
                  ? "Sending…"
                  : whatsappRecipientsLoading
                    ? "Loading…"
                    : label}
              </span>

              {whatsappOptions.length >
              0 ? (
                <span
                  className="fv-whatsapp-menu__caret"
                  aria-hidden="true"
                >
                  ▾
                </span>
              ) : null}
            </button>

            {shareMenu === channel &&
            whatsappOptions.length >
              0 ? (
              <div
                className="fv-whatsapp-menu__dropdown"
                role="menu"
                aria-label={`${label} recipients`}
              >
                <div className="fv-whatsapp-menu__header">
                  <span className="fv-whatsapp-menu__header-title">
                    {channel === "call"
                      ? "Select one number"
                      : "Select recipients"}
                  </span>

                  <span className="fv-whatsapp-menu__header-count">
                    {
                      selectedWhatsappRecipients.length
                    }
                    /{channel === "call" ? 1 : MAX_WHATSAPP_RECIPIENTS}
                  </span>
                </div>

                <div className="fv-whatsapp-menu__list">
                  {whatsappOptions.map(
                    (opt) => {
                      const optionId =
                        String(
                          opt.id
                        );

                      const checked =
                        selectedWhatsappRecipients.includes(
                          optionId
                        );

                      const maxReached =
                        selectedWhatsappRecipients.length >=
                        (channel === "call"
                          ? 1
                          : MAX_WHATSAPP_RECIPIENTS);

                      const disabled =
                        whatsappSending ||
                        shareBusy ||
                        (!checked &&
                          maxReached);

                      return (
                        <label
                          key={`${channel}-${opt.id}-${opt.phone}`}
                          className={`fv-whatsapp-menu__checkbox-item${
                            checked
                              ? " fv-whatsapp-menu__checkbox-item--selected"
                              : ""
                          }${
                            opt.isPrimaryAdmin
                              ? " fv-whatsapp-menu__checkbox-item--primary"
                              : ""
                          }`}
                        >
                          <input
                            type="checkbox"
                            checked={
                              checked
                            }
                            disabled={
                              disabled
                            }
                            onChange={() =>
                              toggleWhatsappRecipient(
                                opt
                              )
                            }
                            aria-label={`Select ${opt.label}`}
                          />

                          <span className="fv-whatsapp-menu__checkbox-content">

                            <span className="fv-whatsapp-menu__item-label">
                              <span>
                                {
                                  opt.label
                                }
                              </span>

                              {opt.isPrimaryAdmin ? (
                                <span className="fv-whatsapp-menu__primary">
                                  Primary Admin
                                </span>
                              ) : null}
                            </span>

                            <span className="fv-whatsapp-menu__item-role">
                              {opt.roleLabel ||
                                opt.role ||
                                "User"}
                            </span>

                            <span className="fv-whatsapp-menu__item-phone">
                              {
                                opt.phone
                              }
                            </span>

                          </span>
                        </label>
                      );
                    }
                  )}
                </div>

                {channel !== "call" &&
                selectedWhatsappRecipients.length >=
                MAX_WHATSAPP_RECIPIENTS ? (
                  <div className="fv-whatsapp-menu__limit">
                    Maximum{" "}
                    {
                      MAX_WHATSAPP_RECIPIENTS
                    }{" "}
                    recipients can be selected.
                  </div>
                ) : null}

                <button
                  type="button"
                  className="fv-whatsapp-menu__send"
                  disabled={
                    shareBusy ||
                    whatsappSending ||
                    selectedWhatsappRecipients.length ===
                      0
                  }
                  onClick={
                    handleSendToSelectedWhatsapp
                  }
                >
                  <BtnIcon />

                  <span>
                    {channel === "call"
                      ? selectedWhatsappRecipients.length === 0
                        ? "Select one number"
                        : "Call"
                      : whatsappSending
                      ? "Sending…"
                      : selectedWhatsappRecipients.length ===
                          0
                        ? "Select recipients"
                        : `Send to ${selectedWhatsappRecipients.length} ${
                            selectedWhatsappRecipients.length ===
                            1
                              ? "recipient"
                              : "recipients"
                          }`}
                  </span>
                </button>
              </div>
            ) : null}
          </div>
  );

  return (
    <div
      className={`fv-detail-overlay${
        overlayClassName
          ? ` ${overlayClassName}`
          : ""
      }`}
      role="presentation"
      onClick={onClose}
    >
      <div
        className={`fv-detail-modal${
          className
            ? ` ${className}`
            : ""
        }`}
        role="dialog"
        aria-modal="true"
        aria-labelledby="fv-detail-title"
        onClick={(e) =>
          e.stopPropagation()
        }
      >
        {/* ----------------------------------------- */}
        {/* HEADER */}
        {/* ----------------------------------------- */}

        <div className="fv-detail-header">
          <div>
            <h2 id="fv-detail-title">
              {formatVehicleNumberDisplay(
                displayItem.vehicleNumber
              )}
            </h2>

            <p className="fv-detail-subtitle">
              {safeValue(
                displayItem.customerName
              )}
            </p>
          </div>

          <button
            type="button"
            className="fv-detail-close"
            onClick={onClose}
            aria-label="Close"
          >
            ×
          </button>
        </div>

        {/* ----------------------------------------- */}
        {/* VEHICLE DETAILS */}
        {/* ----------------------------------------- */}

        {isRepoAdmin ? (
          <AdminExcelFieldGrid
            caseData={displayItem}
            enriching={enriching}
            layout="cards"
            omitLabels={
              ADMIN_GRID_OMIT_LABELS
            }
          />
        ) : (
          <div className="fv-detail-grid">
            {sortedDetailRows.map(
              (row) => (
                <div
                  className={`fv-detail-item${
                    row.key ===
                    "address"
                      ? " fv-detail-item--full"
                      : ""
                  }`}
                  key={row.key}
                >
                  <span className="fv-detail-label">
                    {row.label}
                  </span>

                  <span className="fv-detail-value">
                    {row.value}
                  </span>
                </div>
              )
            )}
          </div>
        )}

        {/* ----------------------------------------- */}
        {/* LOADED DETAILS */}
        {/* ----------------------------------------- */}

        <section
          className="fv-detail-loaded"
          aria-label="What is loaded"
        >
          {loadedError ? (
            <p className="fv-error fv-detail-loaded__error">
              {loadedError}
            </p>
          ) : null}

          <div className="fv-detail-loaded__row">
            <span className="fv-detail-loaded__label">
              Loaded details
            </span>

            <input
              type="text"
              className="fv-detail-loaded__input"
              value={
                loadedDetail
              }
              onChange={(e) => {
                setLoadedDetail(
                  e.target.value
                );

                setLoadedDirty(
                  true
                );
              }}
              onBlur={
                handleSaveLoaded
              }
              placeholder="What is loaded"
              maxLength={2000}
              disabled={
                loadedLoading ||
                loadedSaving
              }
            />

            <span
              className="fv-detail-loaded__status"
              aria-live="polite"
            >
              {loadedSaving
                ? "…"
                : loadedSavedMsg ||
                  (loadedLoading
                    ? "…"
                    : "")}
            </span>
          </div>
        </section>

        {isRepoAdmin ? (
        <div
          className="fv-detail-grid fv-detail-banker-grid"
          aria-label="Bankar names and mobile numbers"
        >
          {BANKER_DETAIL_FIELDS.map(
            (field) => (
              <div
                className="fv-detail-item"
                key={field.key}
              >
                <span className="fv-detail-label">
                  {field.label}
                </span>

                <span className="fv-detail-value">
                  {safeValue(
                    bankerFields[
                      field.key
                    ]
                  )}
                </span>
              </div>
            )
          )}
        </div>
        ) : null}

        {/* ----------------------------------------- */}
        {/* ACTIONS */}
        {/* ----------------------------------------- */}

        <div className="fv-detail-actions">

          {renderPhoneShareMenu(
            "whatsapp",
            whatsappMenuRef,
            FaWhatsapp,
            "fv-btn-whatsapp",
            "WhatsApp"
          )}

          {/* ======================================= */}
          {/* EMAIL */}
          {/* ======================================= */}

          <button
            type="button"
            className="fv-btn fv-btn-email"
            disabled={
              shareBusy ||
              whatsappSending
            }
            onClick={() =>
              onShare?.(
                "email",
                itemWithLoaded
              )
            }
          >
            <FaEnvelope />

            <span>
              {shareBusy
                ? "Saving…"
                : "Email"}
            </span>
          </button>

          {renderPhoneShareMenu(
            "sms",
            smsMenuRef,
            FaSms,
            "fv-btn-sms",
            "SMS"
          )}

          {renderPhoneShareMenu(
            "call",
            callMenuRef,
            FaPhone,
            "fv-btn-call",
            "Call"
          )}
        </div>
      </div>
    </div>
  );
}
